import { applyClassList, Fragment, jsx, setClassToken } from '@amazon/vinyl-tsx'
import { Header, type HeaderDeps } from '@/header/Header'
import { NerdStats } from '@/nerdStats/NerdStats'
import { HeaderMenu } from '@/header/HeaderMenu'
import { getDeviceConfig, isTv } from '@/device/deviceConfig'
import { CodeLinkCallout } from '@/auth/PublicCodePrompt'
import { Transport } from '@/transport/Transport'
import { notSignedIn } from '@/auth/authRef'
import { nerdStatsVisible } from '@/nerdStats/NerdStatsController'
import { applyBgColors, bgColors } from '@/components/BackgroundColors'
import type { Player } from '@/playback/api'
import type { ManagedPlaylistController } from '@/playback/PlaylistController'
import { AsyncComponentLoader } from '@/components/AsyncComponentLoader'
import { modalVisibleClass } from '@/style/modalVisible'
import type { CloudQueueController } from '@/cloudQueue/CloudQueueController'
import { AnonymousBanner } from '@/header/AnonymousBanner'
import { MobileBottomNav } from '@/header/MobileBottomNav'
import { VideoStageOverlay } from '@/video/VideoStageOverlay'
import { ModalLayer } from '@/components/modalLayerHost'
import { onNavigateAttempt } from '@/router/router'
import { enterPip, getHasVideo, getInPip } from '@/video/videoStage'
import { data } from '@amazon/vinyl-observable'
import type { Unsubscribe } from '@amazon/vinyl-util'
import type { PartySessionController } from '@/party/PartySessionController'

export type AppViewDeps = HeaderDeps & {
    readonly playerPromise: Promise<Player>
    readonly playlistController: ManagedPlaylistController
    readonly cloudQueueController: CloudQueueController
    readonly partySessionController: PartySessionController
}

export interface AppViewProps {
    readonly deps: AppViewDeps
}

export function AppView(props: AppViewProps) {
    const { usesMapAuth } = getDeviceConfig()
    const tvAuthPrompt = usesMapAuth ? <MapAuthWaiting /> : isTv() ? <CodeLinkCallout /> : null
    const headerMenuVisible = data(false)

    return (
        <div
            id="fullscreenRoot"
            className="appView"
            classList={[
                props.deps.playlistController.current.map((value) =>
                    value == null ? null : 'showTransport'
                ),
            ]}
            onConnect={(root) => {
                const colorsSub = bgColors.onData((colors) => {
                    applyBgColors(document.body, colors)
                    setClassToken(document.body, 'backgroundRadial', colors != null)
                })
                // In fullscreen the chrome (header + transport +
                // stage buttons) auto-hides after a few seconds of
                // inactivity. Mouse / touch reveals it again. Class
                // toggle keeps the rule local to fullscreen mode.
                let hideTimer: number | null = null
                const HIDE_DELAY_MS = 2500
                const reveal = () => {
                    setClassToken(root, 'idleHideChrome', false)
                    if (hideTimer != null) window.clearTimeout(hideTimer)
                    hideTimer = window.setTimeout(() => {
                        if (document.fullscreenElement === root) {
                            setClassToken(root, 'idleHideChrome', true)
                        }
                    }, HIDE_DELAY_MS)
                }
                const onActivity = () => reveal()
                const onFs = () => {
                    if (document.fullscreenElement === root) {
                        root.addEventListener('mousemove', onActivity)
                        root.addEventListener('touchstart', onActivity)
                        // Enter clean: chrome hidden from the start. The
                        // user's intent in going fullscreen is "show me
                        // the video, not the UI" — they've already
                        // interacted to trigger the transition, so the
                        // standard mouse-move-to-reveal idle timer would
                        // just delay the first 2.5s of immersion.
                        if (hideTimer != null) window.clearTimeout(hideTimer)
                        setClassToken(root, 'idleHideChrome', true)
                    } else {
                        root.removeEventListener('mousemove', onActivity)
                        root.removeEventListener('touchstart', onActivity)
                        if (hideTimer != null) window.clearTimeout(hideTimer)
                        setClassToken(root, 'idleHideChrome', false)
                    }
                }
                document.addEventListener('fullscreenchange', onFs)

                // Any navigation attempt while a video is on the stage
                // is the user's signal that they want to keep watching
                // but interact with another page — kick the singleton
                // <video> into picture-in-picture so it survives the
                // route change. We use `navigateAttempt` (not the
                // post-nav `navigate` event) so that re-clicking the
                // current page also enters PiP.
                const navSub = onNavigateAttempt(() => {
                    if (getHasVideo().value && !getInPip().value) {
                        enterPip().catch(() => {})
                    }
                })
                return () => {
                    colorsSub()
                    document.removeEventListener('fullscreenchange', onFs)
                    root.removeEventListener('mousemove', onActivity)
                    root.removeEventListener('touchstart', onActivity)
                    if (hideTimer != null) window.clearTimeout(hideTimer)
                    navSub()
                }
            }}
        >
            <Header id="header" headerMenuVisible={headerMenuVisible} deps={props.deps} />
            <HeaderMenu visible={headerMenuVisible} />

            <div id="toastContainer" visible={false}>
                <div className="messages"></div>
            </div>
            {tvAuthPrompt}

            <div id="stage"></div>
            <VideoStageOverlay deps={props.deps} />

            {/*
              App-level modal stack. Any caller invokes `openModal`
              with a render thunk; the layer mounts the topmost entry
              here. Centralized so virtualized rows can't orphan
              portalled modals inside `<body>`.
            */}
            <ModalLayer />

            <div id="footer">
                <AnonymousBanner />
                {/* Components that need the player will wait for it to load */}
                <AsyncComponentLoader
                    createComponent={(player) => (
                        <>
                            <NerdStats
                                visible={nerdStatsVisible}
                                deps={{
                                    player,
                                }}
                            />
                            <Transport
                                deps={{
                                    player,
                                    playerPromise: props.deps.playerPromise,
                                    playlistController: props.deps.playlistController,
                                    cloudQueueController: props.deps.cloudQueueController,
                                }}
                            />
                        </>
                    )}
                    loadData={() => props.deps.playerPromise}
                />
                <MobileBottomNav />
            </div>
        </div>
    )
}

export function initializeAppView(props: AppViewProps): Unsubscribe {
    const appView = <AppView {...props} />
    document.body.append(appView)

    // When there's a modal visible, lock scrolling
    applyClassList(document.scrollingElement as HTMLElement, [modalVisibleClass])
    return () => appView.remove()
}

function MapAuthWaiting() {
    return (
        <div className="mapAuthWaiting" visible={notSignedIn()}>
            Waiting for MAP authentication...
        </div>
    )
}
